import { track } from '@verfi/shared/dist/analytics/browser';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Replayer } from 'rrweb';
import { REPLAY_SECURITY_OPTIONS } from '../lib/replay-security';
import apiService from '../services/api';
import 'rrweb/dist/rrweb.min.css';
import '../styles/Player.css';

// Define the global VerfiPiiFields interface for TypeScript
declare global {
  interface Window {
    VerfiPiiFields?: {
      PII_FIELDS: Record<string, string[]>;
      detectPiiInAttributes: (attributes: Record<string, string>) => string | false;
      isPiiContent: (text: string) => string | false;
    }
  }
}

// PII masking configuration
interface PiiMaskingConfig {
  autoDetectedFields: string[];
  maskChar: string;
}

// Define the Player props interface
interface PlayerProps {
  verfiID?: string;
  jsonData?: string;
  events?: any[];
  autoPlay?: boolean;
  showControls?: boolean;
  showDebug?: boolean;
  width?: string | number;
  height?: string | number;
}

// Main Player component
const Player: React.FC<PlayerProps> = ({
  verfiID,
  jsonData,
  events: initialEvents,
  width = '100%',
  height = '100%',
  autoPlay = false,
  showControls = true,
  showDebug = false,
}) => {
  // State for player data and UI
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (error) track('operation_failed', { operation: 'playback', outcome: 'failure' }); }, [error]);
  const [ready, setReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(autoPlay);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [fileName, setFileName] = useState<string>('');
  const [piiConfig, setPiiConfig] = useState<PiiMaskingConfig>({ 
    autoDetectedFields: [], 
    maskChar: '•' 
  });
  
  // Refs for DOM elements and player instance
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const replayerWrapperRef = useRef<HTMLDivElement | null>(null);
  const replayerRef = useRef<any>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  
  // Refs for tracking playback state to prevent initial slider jumps
  const initialPlaybackPhaseRef = useRef<boolean>(true);
  const playbackStartTimeRef = useRef<number>(0);
  const lastTimePositionRef = useRef<number>(0);
  const timerRef = useRef<number | NodeJS.Timeout | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  
  // Function to start the timer for updating the slider during playback
  const startTimer = (duration = totalTime) => {
    // Clear any existing timer to prevent multiple timers
    if (timerRef.current) {
      console.log('Clearing existing timer');
      clearInterval(timerRef.current as NodeJS.Timeout);
      timerRef.current = null;
    }
    
    console.log('Starting timer for slider updates');
    // Use setInterval for consistent updates (33ms ≈ 30fps)
    timerRef.current = setInterval(() => {
      if (replayerRef.current && isPlayingRef.current) {
        try {
          const currentTimeValue = replayerRef.current.getCurrentTime();
          if (typeof currentTimeValue === 'number') {
            setCurrentTime(currentTimeValue);
            
            // Check if we've reached the end of the playback
            if (currentTimeValue >= duration) {
              console.log('Reached end of playback, pausing');
              handlePause();
              setCurrentTime(duration);
            }
          }
        } catch (e) {
          console.warn('Error updating current time in timer:', e);
        }
      }
    }, 33);
    
    console.log('Timer started with ID:', timerRef.current);
  };
  
  // Function to fetch session data from the API
  const fetchSessionFromAPI = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching session data for verfiID: ${id}`);
      
      // Get session details
      const sessionData = await apiService.getSession(id);
      console.log('Session data received:', sessionData);
      
      // Get session events
      const eventsData = await apiService.getSessionEvents(id);
      console.log(`Received ${eventsData.length} events`);
      
      if (!eventsData || !Array.isArray(eventsData) || eventsData.length === 0) {
        throw new Error('No events found in session data');
      }
      
      // Extract auto-detected PII fields from metadata
      if (sessionData.metadata && sessionData.metadata['auto-detected-pii-fields']) {
        const autoDetectedFields = sessionData.metadata['auto-detected-pii-fields'].split(',');
        console.log('Auto-detected PII fields:', autoDetectedFields);
        
        setPiiConfig(prevConfig => ({
          ...prevConfig,
          autoDetectedFields
        }));
        
        // Apply our own masking to the events data
        if (eventsData && Array.isArray(eventsData)) {
          const maskedEvents = maskPiiInEvents(eventsData, autoDetectedFields);
          setEvents(maskedEvents);
        } else {
          setEvents(eventsData);
        }
      } else {
        setEvents(eventsData);
      }
      
      setFileName(`Session ${id}`);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching session:', err);
      setError(`Failed to load session: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setLoading(false);
    }
  }, []);

  // Effect to load session data when verfiID, jsonData, or initialEvents changes
  useEffect(() => {
    // Refresh TypeScript error checking and clear stale errors
    console.log('Refreshing TypeScript error checking...');
    
    if (initialEvents && Array.isArray(initialEvents)) {
      console.log('*** Version 1.1.0 ***');
      console.log(`Using ${initialEvents.length} events passed directly as prop`);
      setEvents(initialEvents);
      setFileName('From events prop');
      setLoading(false);
    } else if (verfiID) {
      console.log(`Loading session data for verfiID: ${verfiID}`);
      fetchSessionFromAPI(verfiID);
    } else if (jsonData) {
      try {
        console.log('Parsing JSON data...');
        const parsedEvents = JSON.parse(jsonData);
        if (Array.isArray(parsedEvents)) {
          setEvents(parsedEvents);
          setFileName('From JSON prop');
        } else {
          setError('Invalid JSON format. Expected an array of events.');
        }
      } catch (err) {
        setError('Failed to parse JSON data.');
      }
    }
  }, [verfiID, jsonData, initialEvents, fetchSessionFromAPI]);

  // Function to mask PII in events data
  const maskPiiInEvents = (events: any[], piiFields: string[]) => {
    console.log(`Processing ${events.length} events for PII masking`);
    console.log(`PII fields to mask:`, piiFields);
    
    // Create a map to track input fields by node ID
    const nodeFieldMap: Record<string, string> = {};
    
    // First pass: identify input fields and their names
    events.forEach(event => {
      // Look for node creation events (type 2) that might be form fields
      if (event.type === 2 && event.data && event.data.node) {
        const node = event.data.node;
        if (node && (node.tagName === 'INPUT' || node.tagName === 'TEXTAREA')) {
          const nodeId = event.data.id;
          const name = node.attributes?.name || node.attributes?.id || '';
          
          // Store all input fields with their names for later reference
          if (name) {
            nodeFieldMap[nodeId] = name;
            
            // Log if this is a PII field we're tracking
            if (piiFields.includes(name)) {
              console.log(`Identified PII field: "${name}" with node ID: ${nodeId}`);
            }
          }
        }
      }
    });
    
    // Second pass: mask PII in events
    return events.map(event => {
      // Deep clone the event to avoid modifying the original
      const clonedEvent = JSON.parse(JSON.stringify(event));
      
      // Process type 3 events (which might contain input data)
      if (event.type === 3) {
        const nodeId = event.data?.id;
        let textContent = '';
        let textLocation = '';
        
        // Extract text content from the event
        if (event.data?.payload?.text) {
          textContent = event.data.payload.text;
          textLocation = 'payload.text';
        } else if (event.data?.text) {
          textContent = event.data.text;
          textLocation = 'text';
        } else if (event.data?.value) {
          textContent = event.data.value;
          textLocation = 'value';
        }
        
        if (textContent && nodeId) {
          const fieldName = nodeFieldMap[nodeId] || '';
          
          // Check if this field is in our auto-detected PII fields list
          const isPiiField = piiFields.includes(fieldName);
          
          if (isPiiField) {
            // Mask the text content
            const maskedText = '•'.repeat(textContent.length);
            console.log(`Masking PII value in field "${fieldName}": "${textContent.substring(0, 3)}..." -> "${maskedText.substring(0, 3)}..."`);
            
            // Update the text content in the appropriate location
            if (textLocation === 'payload.text' && clonedEvent.data?.payload) {
              clonedEvent.data.payload.text = maskedText;
            } else if (textLocation === 'text') {
              clonedEvent.data.text = maskedText;
            } else if (textLocation === 'value') {
              clonedEvent.data.value = maskedText;
            }
          }
        }
      }
      
      return clonedEvent;
    });
  };
  
  // Create a global style element for rrweb to use
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const rrwebStyleId = 'rrweb-style-container';
      if (!document.getElementById(rrwebStyleId)) {
        const styleEl = document.createElement('style');
        styleEl.id = rrwebStyleId;
        document.head.appendChild(styleEl);
      }
    }
  }, []);
  
  // Effect to initialize player when events are loaded
  useEffect(() => {
    // Clean up function to handle component unmounting
    let mounted = true;
    setReady(false);
    setIsPlaying(false);
    isPlayingRef.current = false;
    let initTimeout: number | null = null;
    
    const safeInitialize = () => {
      if (!mounted || !playerContainerRef.current || events.length === 0) {
        return;
      }
      
      // Reset tracking variables for clean playback
      initialPlaybackPhaseRef.current = true;
      playbackStartTimeRef.current = 0;
      lastTimePositionRef.current = 0;
      
      try {
        // Ensure the document is ready before initializing
        if (document.readyState === 'complete') {
          // Handle async initialization
          initializePlayer().catch(err => {
            console.error('Async initialization error:', err);
            setError(`Failed to initialize player: ${err instanceof Error ? err.message : 'Unknown error'}`);
          });
        } else {
          // Wait for document to be fully loaded
          window.addEventListener('load', safeInitialize, { once: true });

        }
      } catch (err) {
        console.error('Error during safe initialization:', err);
        setError(`Failed to initialize player safely: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };
    
    if (events.length > 0 && !loading && playerContainerRef.current) {
      // Delay initialization slightly to ensure DOM is ready
      initTimeout = window.setTimeout(safeInitialize, 300);
    }
    
    // Cleanup function
    return () => {
      mounted = false;
      window.removeEventListener('load', safeInitialize);
      if (timerRef.current) {
        clearInterval(timerRef.current as NodeJS.Timeout);
        timerRef.current = null;
      }
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
      if (replayerRef.current) {
        try {
          replayerRef.current.destroy();
          replayerRef.current = null;
        } catch (err) {
          console.error('Error during cleanup:', err);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, playerContainerRef.current]);
  

  
  // Function to initialize the player
  const initializePlayer = async () => {
    console.log('Initializing player with events:', events.length);
    if (!playerContainerRef.current || events.length === 0) {
      console.log('Cannot initialize player: missing container or events');
      return;
    }
    
    // Clean up any existing player - with error handling
    if (replayerRef.current) {
      try {
        replayerRef.current.pause();
      } catch (err) {
        console.warn('Error pausing existing player:', err);
      }
      replayerRef.current = null;
    }
    
    if (replayerWrapperRef.current && replayerWrapperRef.current.parentNode) {
      replayerWrapperRef.current.parentNode.removeChild(replayerWrapperRef.current);
      replayerWrapperRef.current = null;
    }
    
    // Get the player container
    const playerContainer = playerContainerRef.current;
    
    // Find the meta event to get original recording dimensions
    const metaEvent = events.find(e => e.type === 4);
    let width = 1024; // Default width
    let height = 768; // Default height
    
    if (metaEvent && metaEvent.data && metaEvent.data.width && metaEvent.data.height) {
      width = metaEvent.data.width;
      height = metaEvent.data.height;
      console.log(`Original recording dimensions: ${width}x${height}`);
    }
    
    // Create a wrapper for the replayer with exact dimensions matching the original recording
    // First, clear any existing content in the player container
    while (playerContainer.firstChild) {
      playerContainer.removeChild(playerContainer.firstChild);
    }
    
    const replayerWrapper = document.createElement('div');
    replayerWrapper.className = 'recording-surface';
    
    // Preserve the recorded viewport. The parent viewport scrolls when needed;
    // never resize the replay iframe independently of its cursor coordinate plane.
    replayerWrapper.style.width = `${width}px`;
    replayerWrapper.style.height = `${height}px`;
    replayerWrapper.style.position = 'relative';
    replayerWrapper.style.margin = '0 auto';

    playerContainer.appendChild(replayerWrapper);
    replayerWrapperRef.current = replayerWrapper;
    
    // For rrweb 2.0.0-alpha.4, use the privacy option
    const playerConfig = {
      root: replayerWrapper,
      skipInactive: false,
      showWarning: false,  // Changed to match POC v2.6
      showDebug: false,
      mouseTail: {
        duration: 500,
        lineCap: 'round',
        lineWidth: 3,
        strokeStyle: '#007bff'  // Professional blue color matching design system
      },
      ...REPLAY_SECURITY_OPTIONS,
      // These are the critical settings from POC v2.6
      width: width,
      height: height,
      scaling: 1,
      viewportResizingStrategy: 'no-scaling', // Critical - prevents rrweb's internal scaling
      mousePosFix: true, // Ensures mouse coordinates are accurate
      // Configuration that worked in POC v2.6
      blockClass: 'no-record', // Keep it simple
      inlineStylesheet: true,
      triggerFocus: false,
      pauseAnimation: false,
      
      // For rrweb 2.0.0-alpha.4, privacy settings must be under a privacy object
      privacy: {
        maskAllInputs: false,
        // Add logging for privacy configuration
        logHandler: (level: string, message: string, payload?: any) => {
          console.log(`🔒 rrweb privacy ${level}: ${message}`, payload);
        },
        // Define custom masking function for inputs
        maskInputFn: (input: HTMLElement) => {
          // Extract element attributes
          const name = input.getAttribute('name') || '';
          const id = input.getAttribute('id') || '';
          const placeholder = input.getAttribute('placeholder') || '';
          const type = input.getAttribute('type') || '';
          
          console.log(`🔍 Checking element for PII masking - name: "${name}", id: "${id}", type: "${type}", placeholder: "${placeholder}"`);
          console.log(`🔍 Auto-detected PII fields:`, piiConfig.autoDetectedFields);
          
          // Always mask password fields
          if (type === 'password') {
            console.log(`🎭 MASKING password field: "${name || id}"`);
            return true;
          }
          
          // Check against auto-detected PII fields
          for (const field of piiConfig.autoDetectedFields) {
            if (name === field || id === field || 
                name.includes(field) || id.includes(field) ||
                placeholder === field || placeholder.includes(field)) {
              console.log(`🎭 MASKING auto-detected PII field: "${field}" (matched with "${name || id || placeholder}")`);
              return true;
            }
          }
          
          // Use the shared PII module
          const attributes = { name, id, placeholder, type };
          const piiCategory = window.VerfiPiiFields?.detectPiiInAttributes(attributes);
          
          if (piiCategory) {
            console.log(`🎭 MASKING detected PII field (${piiCategory}): "${name || id}"`);
            return true;
          }
          
          console.log(`✓ Not a PII field: "${name || id || placeholder}"`);
          return false;
        },
        // Additional CSS selectors for auto-detected fields
        maskTextSelector: {
          div: piiConfig.autoDetectedFields.map(field => 
            `[data-field="${field}"], [data-name="${field}"], .${field}-field, .${field}-input`
          ).join(', ')
        },
        // CSS selectors for input elements that should be masked
        maskInputSelector: {
          input: [
            // Auto-detected fields from session metadata
            ...piiConfig.autoDetectedFields.map(field => 
              `[name="${field}"], [id="${field}"], [placeholder*="${field}"], [data-field="${field}"]`
            ),
            // Known PII field patterns from the shared module
            ...Object.values(window.VerfiPiiFields?.PII_FIELDS || {}).flat().map(field => 
              `[name="${field}"], [id="${field}"], [name*="${field}"], [id*="${field}"]`
            ),
            // Always mask password fields
            '[type="password"]'
          ].join(', ')
        }
      },
      plugins: [],
    } as any;
    
    // Create the replayer with a direct approach from POC v2.6
    try {
      console.log('Creating Replayer with configuration:', playerConfig);
      
      // Make sure the wrapper is in the DOM before creating the Replayer
      if (!replayerWrapper.isConnected) {
        console.error('Replayer wrapper is not connected to the DOM');
        setError(`Failed to initialize player: DOM elements not ready`);
        return;
      }
      
      // Enhanced validation for events data
      if (!Array.isArray(events) || events.length === 0) {
        console.error('Invalid events data: empty or not an array');
        setError(`Failed to initialize player: Invalid events data`);
        return;
      }
      
      // Validate events to prevent 'Cannot read properties of undefined (reading 'type')' error
      const validatedEvents = events.filter(event => {
        // Ensure each event is a valid object with required properties
        return event && 
               typeof event === 'object' && 
               'type' in event && 
               typeof event.type === 'number' && 
               'timestamp' in event;
      });
      
      console.log('Processing events...');
      
      if (validatedEvents.length < events.length) {
        console.warn(`Filtered out ${events.length - validatedEvents.length} invalid events`);
      }
      
      if (validatedEvents.length === 0) {
        console.error('No valid events after filtering');
        setError(`Failed to initialize player: No valid events found`);
        return;
      }
      
      // Check for at least one full snapshot to prevent the "Cannot read properties of undefined (reading 'type')" error
      const hasFullSnapshot = validatedEvents.some(event => 
        event.type === 2 && event.data && event.data.source !== undefined
      );
      
      if (!hasFullSnapshot) {
        console.warn('No full snapshot found in events, adding safety checks');
        // We'll continue but with extra caution
      }
      
      // Create the replayer with the config and validated events
      replayerRef.current = new Replayer(validatedEvents, playerConfig);
      console.log('Replayer created successfully with raw data');
      
      // Simpler approach to handle the rebuildFullSnapshot error
      // Just wrap the method with a try-catch to prevent errors from bubbling up
      if (replayerRef.current && typeof replayerRef.current.rebuildFullSnapshot === 'function') {
        const originalRebuild = replayerRef.current.rebuildFullSnapshot;
        replayerRef.current.rebuildFullSnapshot = function(...args: any[]) {
          try {
            return originalRebuild.apply(this, args);
          } catch (error) {
            console.warn('Protected against rebuildFullSnapshot error:', error);
            // Return a safe value instead of throwing
            return {};
          }
        };
      }
      
      // rrweb pause(offset) synchronously casts the initial snapshot and pauses.
      // Do not expose controls before this reset, or schedule a later reset that
      // could stop a user's first Play click.
      replayerRef.current.pause(0);
    } catch (error) {
      console.error('Error creating rrweb Replayer:', error);
      setError(`Failed to initialize player: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return;
    }
    
    // Set up player event listeners with error handling
    if (replayerRef.current) {
      try {
        // Initialize player state
        const metadata = replayerRef.current.getMetaData();
        if (metadata && typeof metadata.totalTime === 'number') {
          // Calculate the actual total time by finding the last event timestamp
          let lastEventTime = 0;
          if (events.length > 0) {
            // Get the timestamp of the last event
            const lastEvent = events[events.length - 1];
            if (lastEvent && typeof lastEvent.timestamp === 'number') {
              lastEventTime = lastEvent.timestamp;
            }
            
            // Get the timestamp of the first event to normalize
            const firstEvent = events[0];
            let firstEventTime = 0;
            if (firstEvent && typeof firstEvent.timestamp === 'number') {
              firstEventTime = firstEvent.timestamp;
            }
            
            // Calculate the actual duration (last - first)
            const actualDuration = Math.max(0, lastEventTime - firstEventTime);
            
            // Use the calculated duration if it's valid, otherwise fall back to metadata
            if (actualDuration > 0) {
              setTotalTime(actualDuration);
            } else {
              setTotalTime(metadata.totalTime);
            }
          } else {
            setTotalTime(metadata.totalTime);
          }
        } else {
          console.warn('Invalid metadata from replayer:', metadata);
          setTotalTime(0);
        }
        
        // We'll use a manual approach for handling events since addEventListener may not be available
        // or may not work consistently across all rrweb versions
        try {
          // Store references to these functions for use in handlePlay and handlePause
          // This is a cleaner approach than trying to use addEventListener
          console.log('Setting up manual event handling');
          
          // We won't try to use addEventListener at all - instead we'll call these functions
          // directly from our handlePlay and handlePause functions
          
          // This approach is more reliable and avoids the warning about missing addEventListener
        } catch (setupError) {
          console.error('Error setting up event handling:', setupError);
          // Continue execution - don't block on handler errors
        }
        
        setCurrentTime(0);
        if (autoPlay) {
          replayerRef.current.play(0);
          setIsPlaying(true);
          track('proof_played');
          isPlayingRef.current = true;
          startTimer(metadata.totalTime);
        }
        setReady(true);

      } catch (setupError) {
        console.error('Error setting up player event listeners:', setupError);
        setError('Failed to initialize playback');
        return undefined;
      }
    }
  };
  
  // Restart playback from the beginning
  const handleRestart = () => {
    if (replayerRef.current) {
      try {
        // Pause first
        replayerRef.current.pause();
        
        // Try goto first, then fallback to play(0)
        let restartSuccessful = false;
        if (typeof replayerRef.current?.goto === 'function') {
          replayerRef.current.goto(0, true);
          restartSuccessful = true;
        }
        
        if (!restartSuccessful) {
          replayerRef.current.play(0);
        } else {
          // If goto worked, start playing
          replayerRef.current.play();
        }
        
        // Update UI state
        setIsPlaying(true); track('proof_played');
        setCurrentTime(0);
        isPlayingRef.current = true;
        startTimer();
      } catch (error) {
        console.error('Error restarting:', error);
        setError(`Failed to restart: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handlePlay = () => {
    if (replayerRef.current) {
      try {
        console.log('Play button clicked, attempting to play...');
        
        // Reset tracking variables for clean playback when resuming
        initialPlaybackPhaseRef.current = true;
        playbackStartTimeRef.current = Date.now();
        
        // Get current time before playing to resume from the same position
        let currentTimeValue = 0;
        try {
          if (typeof replayerRef.current?.getCurrentTime === 'function') {
            currentTimeValue = replayerRef.current.getCurrentTime();
            console.log('Current time before play:', currentTimeValue);
          }
        } catch (timeError) {
          console.warn('Error getting current time, defaulting to 0:', timeError);
        }
        
        // Set last time position to current time to prevent jumps
        if (typeof currentTimeValue === 'number') {
          lastTimePositionRef.current = currentTimeValue;
        }
        
        // Simple two-step approach: try play with time, then fallback to simple play
        let playSuccessful = false;
        if (typeof replayerRef.current?.play === 'function') {
          try {
            replayerRef.current.play(currentTimeValue);
            playSuccessful = true;
            console.log('Play with explicit time succeeded');
          } catch (playError) {
            console.warn('Play with explicit time failed, trying simple play:', playError);
            try {
              replayerRef.current.play();
              playSuccessful = true;
              console.log('Simple play succeeded');
            } catch (simplePlayError) {
              console.warn('Simple play also failed:', simplePlayError);
            }
          }
        }
        
        if (playSuccessful) {
          // Update UI state - single call, no multiple setIsPlaying
          setIsPlaying(true); track('proof_played');
          isPlayingRef.current = true;
          console.log('Play successful, UI state updated');
          
          // Force an immediate update of the current time
          try {
            const immediateTimeValue = replayerRef.current.getCurrentTime();
            if (typeof immediateTimeValue === 'number') {
              setCurrentTime(immediateTimeValue);
              console.log('Forced immediate time update to:', immediateTimeValue);
            }
          } catch (e) {
            console.warn('Error getting immediate time update:', e);
          }
          
          // Start the timer for updating the slider during playback
          startTimer();
        } else {
          console.error('All play attempts failed');
          setError('Failed to start playback');
        }
      } catch (error) {
        console.error('Error in handlePlay:', error);
        setError(`Failed to play: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };
  
  const handlePause = () => {
    if (replayerRef.current) {
      try {
        // First update the playing state to stop animation frames
        setIsPlaying(false);
        isPlayingRef.current = false; // Update ref for animation frame management
        
        // Cancel any existing animation frame
        if (timerRef.current) {
          clearInterval(timerRef.current as NodeJS.Timeout);
          timerRef.current = null;
        }
        
        // Then pause the replayer
        replayerRef.current.pause();
        
        // Force a single update of the current time when paused
        const currentTimeValue = replayerRef.current.getCurrentTime();
        if (typeof currentTimeValue === 'number') {
          setCurrentTime(currentTimeValue);
        }
      } catch (error) {
        console.error('Error pausing:', error);
        // Don't show user-facing error for pause failures
      }
    }
  };
  
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const percentage = parseInt(e.target.value, 10);
    if (replayerRef.current && !isNaN(percentage) && totalTime > 0) {
      // Convert percentage to time
      const time = Math.floor((percentage / 100) * totalTime);
      
      // Log the seek operation for debugging
      console.log(`Seeking to ${time}ms (${percentage}%)`);
      
      try {
        // Reset tracking variables for clean playback after seeking
        initialPlaybackPhaseRef.current = true;
        playbackStartTimeRef.current = 0;
        lastTimePositionRef.current = time; // Set to target time to prevent jumps
        
        // Store the current playing state
        const wasPlaying = isPlaying;
        
        // Pause temporarily for seeking
        replayerRef.current.pause();
        
        // Try multiple seeking approaches to ensure reliability
        // This is a critical fix from POC v2.6 that prevents seeking errors
        let seekSuccessful = false;
        
        // First try the standard goto method
        if (typeof replayerRef.current?.goto === 'function') {
          try {
            replayerRef.current.goto(time, true); // Use check=true for better reliability
            seekSuccessful = true;
          } catch (gotoError) {
            console.warn('Primary seek method failed, trying fallback:', gotoError);
            // If goto fails, we'll try the play method below
          }
        }
        
        // If goto failed or isn't available, use play with time parameter
        if (!seekSuccessful && typeof replayerRef.current?.play === 'function') {
          replayerRef.current.play(time);
          seekSuccessful = true;
          
          // If we used play(time), we're already playing
          if (!wasPlaying) {
            // If we weren't playing before, pause again after a short delay
            setTimeout(() => {
              if (replayerRef.current) {
                replayerRef.current.pause();
                setIsPlaying(false);
              }
            }, 50);
          }
        }
        
        // Update the current time
        setCurrentTime(time);
        
        // If it was playing before seeking and we used goto, resume playback
        if (wasPlaying && seekSuccessful && typeof replayerRef.current?.goto === 'function') {
          // Small delay to ensure the seek completes before resuming
          setTimeout(() => {
            if (replayerRef.current) {
              replayerRef.current.play();
            }
          }, 50);
        }
      } catch (error) {
        console.error('Error seeking:', error);
        setError(`Failed to seek: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };
  
  // Format time for display (mm:ss)
  const formatTime = (ms: number) => {
    // Ensure time is not negative
    const normalizedMs = Math.max(0, ms);
    const seconds = Math.floor(normalizedMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Render the player
  return (
    <div className="player-container" style={{ width, height }}>
      <div className="player-header">
        <div className="player-header-text">Session Replay</div>
      </div>
      <div className="player-viewport" ref={playerContainerRef}></div>
      {loading ? (
        <div className="player-loading">Loading session data...</div>
      ) : error ? (
        <div className="player-error">{error}</div>
      ) : (
        <>
          {showControls && (
            <div className="player-controls" ref={controlsRef}>
              <div className="control-buttons">
                <button onClick={handlePlay} disabled={!ready || isPlaying}>
                  Play
                </button>
                <button onClick={handlePause} disabled={!ready || !isPlaying}>
                  Pause
                </button>
                <button onClick={handleRestart} disabled={!ready}>
                  Restart
                </button>
              </div>
              
              <div className="seek-container">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={(totalTime > 0 ? (currentTime / totalTime) * 100 : 0).toString()}
                  onChange={handleSeek}
                  className="player-progress"
                  disabled={!ready}
                />
              </div>
              
              <div className="player-time">
                {formatTime(currentTime)} / {formatTime(totalTime)}
              </div>
            </div>
          )}
          

          
          {showDebug && (
            <div className="player-debug">
              <div>File: {fileName || 'No file loaded'}</div>
              <div>Events: {events.length}</div>
              <div>Total Time: {formatTime(totalTime)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Player;
